import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import './ShoppingCartStyle.scss';
import NavigateBeforeIcon from '@mui/icons-material/NavigateBefore';
import { Button, Typography, Alert } from '@mui/material';
import ProductList from './ProductList';
import PaymentGrid from '../common/Payment/Payment';
import { useCartCalculations } from '../../hooks/useCartCalculations';
import { ErrorOutline } from '@mui/icons-material';
import { clearCoupon } from "../../redux/actions/CartActions";
import { useDispatch, useSelector } from "react-redux";

function ShoppingCart() {
  const dispatch = useDispatch();

  const navigate = useNavigate();
  const endorsements =
    useSelector((state) => state.wizardLaunch?.endorsements) || [];
  const moreClassSelection =
    useSelector((state) => state.wizardLaunch?.moreClassSelection) || [];
  const directCourses =
    useSelector((state) => state.wizardLaunch?.directCourses) || [];
  const previousStep =
    useSelector((state) => state.wizardLaunch?.previousStep) || "/";
  const { userExistingCourse } =
    useSelector((state) => state.user) || [];

  const [updatedMoreClassSectionCourses, setUpdatedMoreClassSectionCourses] =
    useState(moreClassSelection);
  const [updatedEndorsementCourses, setUpdatedEndorsementCourses] =
    useState(endorsements);
  const [updatedDirectCourses, setUpdatedDirectCourses] = useState(directCourses);

  const {
    subtotal = 0,
    discount = 0,
    total = 0,
    hasInstructorLed,
    selfPacedCourses,
  } = useCartCalculations() || {};

  const handleButtonClick = useCallback(() => {
      dispatch(clearCoupon());

    navigate(previousStep);
  }, [navigate, previousStep]);

  // Check if any valid section IDs exist
  const hasValidSectionIds = [...endorsements, ...moreClassSelection, ...directCourses].some(
    (item) => item.section_id
  );

  // Convert values to numbers to ensure they're numeric
  const numericSubtotal = Number(subtotal) || 0;
  const numericDiscount = Number(discount) || 0;
  const numericTotal = Number(total) || 0;

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'instant' });
}, []);
  
  useEffect(() => {
    const purchasedSectionIds =
      userExistingCourse?.purchased_course?.section_data
        ?.filter((course) => Number(course.no_of_days) > 0)
        .map((course) => course.section_id) || [];

      const mappedCourses = moreClassSelection.map((course) => ({
        ...course,
        alreadyPurchased: purchasedSectionIds.includes(course.section_id),
      }));
      // Check if the mapped courses are different from current state
      if (JSON.stringify(mappedCourses) !== JSON.stringify(updatedMoreClassSectionCourses)) {
        setUpdatedMoreClassSectionCourses(mappedCourses);
      }

      const endorsementsCourses = endorsements.map((course) => ({
        ...course,
        alreadyPurchased: purchasedSectionIds.includes(course.section_id),
      }));
      // Check if the mapped courses are different from current state
      if (JSON.stringify(endorsementsCourses) !== JSON.stringify(updatedEndorsementCourses)) {
        setUpdatedEndorsementCourses(endorsementsCourses);
      }

      const directCoursesMapped = directCourses.map((course) => ({
        ...course,
        alreadyPurchased: purchasedSectionIds.includes(course.section_id),
      }));
      // Check if the mapped courses are different from current state
      if (JSON.stringify(directCoursesMapped) !== JSON.stringify(updatedDirectCourses)) {
        setUpdatedDirectCourses(directCoursesMapped);
      }
  }, [userExistingCourse, moreClassSelection, endorsements, directCourses]);

  const hasPurchasedMoreClassSectionCourse = useMemo(
    () =>
      updatedMoreClassSectionCourses.some((course) => course.alreadyPurchased),
    [updatedMoreClassSectionCourses]
  );

  const hasPurchasedEndorsementCourse = useMemo(
    () =>
      updatedEndorsementCourses.some((course) => course.alreadyPurchased),
    [updatedEndorsementCourses]
  );

  const hasPurchasedDirectCourse = useMemo(
    () =>
      updatedDirectCourses?.some((course) => course.alreadyPurchased),
    [updatedDirectCourses]
  );

  return (
    <div className="shopingCart-wrap">
      <div className="heading-sec">
        <Button
          className="back-btn"
          onClick={handleButtonClick}
          startIcon={<NavigateBeforeIcon />}
        >
          Back
        </Button>
        <Typography component="h1" className="heading">
          Shopping Cart
        </Typography>
      </div>

      {(hasPurchasedMoreClassSectionCourse ||
        hasPurchasedEndorsementCourse ||
        hasPurchasedDirectCourse) && (
        <Alert
          icon={<ErrorOutline sx={{ color: "#1B56BB", fontSize: "40px" }} />}
          severity="error"
          sx={{
            mb: 2,
            bgcolor: "white",
            color: "#1B56BB",
            padding: "50px 100px",
            fontSize: "24px",
            textAlign: "center",
            fontWeight: 500,
          }}
        >
          These courses are already in your account or are not eligible for
          purchase. Please review your selections.
        </Alert>
      )}
      {!hasValidSectionIds && (
        <Alert severity="error" sx={{ mb: 2 }}>
          No valid course mapping found. Please select courses with valid
          section IDs.
        </Alert>
      )}
      <div className="grid-container">
        <div className="left-grid-container">
          <ProductList
            endorsements={updatedEndorsementCourses}
            courses={updatedMoreClassSectionCourses}
            directCourses={updatedDirectCourses}
            hasInstructorLed={hasInstructorLed}
          />
        </div>
        <PaymentGrid
          subtotal={numericSubtotal}
          discount={numericDiscount}
          total={numericTotal}
          disabled={!hasValidSectionIds}
          disableContinueButton={
            hasPurchasedMoreClassSectionCourse || 
            hasPurchasedEndorsementCourse ||
            hasPurchasedDirectCourse
          }
        />
      </div>
    </div>
  );
}

export default ShoppingCart;
